import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Survey } from 'components/Survey';
import { QUERY_KEYS } from 'constants/common';
import { SURVEY_TYPES } from 'constants/surveys';
import { getCurrentQuestionIndex } from 'contexts/Survey/utils';
import { useGetSurveyStatus } from 'hooks/useGetSurveyStatus';
import { useGetSurveys } from 'hooks/useGetSurveys';
import { SurveyService } from 'services/surveys';
import { BarometerCard } from './BarometerCard';
import { useBarometerChartData } from './useBarometerChartData';

const props = {
  surveyType: SURVEY_TYPES.TEST,
  useSurveyName: true,
  allowRepeat: true,
  endCardTitle: t('BarometerResults.heading', { ns: LOCIZE_NS.WELL_BEING }),
  endCardSubtitle: t('BarometerResults.body', { ns: LOCIZE_NS.WELL_BEING }),
};

export const WellBeingBarometer = (): React.ReactElement => {
  const { data: surveys, isLoading: isLoadingSurveys } = useGetSurveys(SURVEY_TYPES.TEST);
  const { data: chartData, isLoading: isLoadingChart } = useBarometerChartData();
  const [isTestActive, setIsTestActive] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const barometerSurvey = surveys && surveys.length > 0 ? surveys[surveys.length - 1] : undefined;
  const queryClient = useQueryClient();
  const { isLoading: isLoadingResponse, surveyResponse } = useGetSurveyStatus(
    SURVEY_TYPES.TEST,
    true,
    barometerSurvey?.id
  );

  const isLoading = isLoadingSurveys || isLoadingChart || isLoadingResponse;
  const hasChartData = chartData && chartData.length > 0;

  const surveyState = surveyResponse ?? barometerSurvey;
  const progressIndex = getCurrentQuestionIndex(surveyState);
  const answersCount = surveyResponse?.answers?.length ?? barometerSurvey?.answers?.length ?? 0;
  const isSurveyInProgress = isTestActive || progressIndex > 0 || answersCount > 0;
  const isInitialLoading = isLoading && !isSurveyInProgress;

  if (isInitialLoading || !barometerSurvey) {
    return <LoadingScreen />;
  }

  const handleCompleted = (): void => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.FECTH_BAROMETER_HISTORY, barometerSurvey?.id],
    });
    setIsTestActive(false);
    setIsTestCompleted(true);
  };

  const handleStartTest = async (): Promise<void> => {
    const isCompleted =
      surveyResponse?.completed ??
      surveyResponse?.isCompleted ??
      barometerSurvey.isCompleted ??
      false;

    if (isCompleted) {
      try {
        await SurveyService.getSurveyResponses(barometerSurvey.id);
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.FETCH_SURVEY_RESPONSES, barometerSurvey.id],
        });
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.FETCH_SURVEYS, SURVEY_TYPES.TEST],
        });
      } catch {
        return;
      }
    }

    setIsTestActive(true);
  };

  const surveyProps = {
    surveyId: barometerSurvey.id,
    title: barometerSurvey.name,
    introduction: barometerSurvey.introduction,
    onCompleted: handleCompleted,
    ...props,
  };

  if (isSurveyInProgress || !hasChartData) {
    return <Survey {...surveyProps} />;
  }

  return (
    <div className="flex flex-col gap-8">
      <BarometerCard
        isTestCompleted={isTestCompleted}
        onStartTest={() => {
          void handleStartTest();
        }}
      />
    </div>
  );
};
